import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { ProductImageMissingVisualConfig } from '@epicuro-next/ataraxia/product-image';
import { CopyCoordinates } from '@epicuro-next/features/advanced-mdo-grid';
import { plural } from '@epicuro-next/platform/i18n';
import { COLLAPSED, EXPANDED } from '@epicuro-next/ui';
import { getDeliveryShopId } from '@epicuro-next/utilities';
import {
  BasketAccountAddress,
  Prepack,
  PrepackUnit,
} from 'graphql-api/gql-api-interfaces';

import { BaseAdvancedGridComponent } from '../base-advanced-grid/base-advanced-grid.component';

const MAX_INPUT_VALUE = 9999;

@Component({
  selector: 'epc-advanced-pre-pack',
  templateUrl: './advanced-pre-pack.component.html',
  styleUrls: ['./advanced-pre-pack.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AdvancedPrePackComponent extends BaseAdvancedGridComponent {
  @Input() public prePacks: Prepack[];
  @Input() public showAddNote: boolean;
  @Input() public totalItemsInPrepack: number;
  @Input() public quantityWarningThreshold = 5;
  @Input()
  public productImageMissingVisualConfig?: ProductImageMissingVisualConfig;
  @Output() public noteChanged: EventEmitter<{ id: string; note: string }> =
    new EventEmitter();
  @Output()
  public prepackSelected = new EventEmitter<string>();

  public readonly displayPrepackModeLabel = $localize`:Config Strip|Label for displayBy prepack toggle@@displayByTogglePrepack:Pre-packs`;

  public collapseState(opened: boolean) {
    return opened ? EXPANDED : COLLAPSED;
  }

  public getDeliveryItems(prepack: Prepack) {
    return prepack.quantities.reduce(
      (acc: { items: { [id: string]: number } }, prepackUnit: PrepackUnit) => {
        return {
          items: {
            ...acc.items,
            [prepackUnit.productId]: { quantity: prepackUnit.quantity },
          },
        };
      },
      { items: {} },
    );
  }

  private getPrepackQuantities(prepack: Prepack) {
    return prepack.quantities.reduce(
      (acc: number, prepackUnit: { quantity: number }) => {
        return acc + prepackUnit.quantity;
      },
      0,
    );
  }

  public totalItemsInPrepactText(prepack: Prepack) {
    const totalItems = this.getPrepackQuantities(prepack);
    return plural(
      totalItems,
      {
        1: $localize`:ConfigStrip|prepack items count@@prepacItemsCountOne:${totalItems} item`,
      },
      $localize`:ConfigStrip|prepack items count@@prepacItemsCountMore:${totalItems} items`,
    );
  }

  public getQuantity(accountId: string, prepackId: string) {
    return this.deliveryItems[accountId]?.[prepackId]?.quantity || '';
  }

  public getNote(accountId: string, prepackId: string) {
    return this.deliveryItems[accountId]?.[prepackId]?.note || '';
  }

  public getMaxQuantity(
    prepack: Prepack,
    shipTo: BasketAccountAddress & { id: string },
  ) {
    const result =
      +this.getQuantityLeft(prepack) +
      (this.getQuantity(shipTo.id, prepack.productId) || 0);
    return result === Number.POSITIVE_INFINITY ? MAX_INPUT_VALUE : result;
  }

  public onCopy({ from, to }: CopyCoordinates) {
    const row = this.prePacks[from.y];
    const column = this.shipTos[from.x];

    const xDelta = from.x < to.x ? 1 : -1;
    const yDelta = from.y < to.y ? 1 : -1;
    const rows = [];
    for (
      let rowIndex = from.y;
      yDelta > 0 ? rowIndex <= to.y : rowIndex >= to.y;
      rowIndex += yDelta
    ) {
      rows.push(this.prePacks[rowIndex].productId);
    }
    const columns = [];
    for (
      let colIndex = from.x;
      xDelta > 0 ? colIndex <= to.x : colIndex >= to.x;
      colIndex += xDelta
    ) {
      columns.push(
        getDeliveryShopId({
          accountId: this.shipTos[colIndex].accountId,
          addressId: this.shipTos[colIndex].addressId,
        }),
      );
    }
    const copyPayload = {
      from: {
        productId: row.productId,
        shopId: getDeliveryShopId({
          accountId: column.accountId,
          addressId: column.addressId,
        }),
      },
      to: { shops: columns, products: rows },
    };
    this.copyQuantities.emit(copyPayload);
  }

  public onQuantityInput(
    prepack: Prepack,
    account: BasketAccountAddress,
    value: string | number,
  ) {
    this.prepackSelected.emit(prepack.productId);
    this.quantityChanged.emit({
      productId: prepack.productId,
      shop: account,
      value: +value,
    });
  }

  public onRowClicked(row: Prepack & { opened: boolean }) {
    row.opened = !row.opened;
    this.prepackSelected.emit(row.productId);
  }
}
